/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import { splitAuthoredPath, splitAtEachPosition, LAST_POSITION } from '@own-path';
import { AD_TARGET_FIELDS, INBOUND_TARGET_ROOTS } from '@destination-field';
import { artifactFieldDeclaration, type ArtifactFieldDeclaration } from '@artifact-field-class';
import {
  CONTEXT_ROOTS,
  RESOLUTION_SITES,
  canonicalizeContextPath,
  isOpenContextRoot,
  knowableAt,
} from '@context-roots';
import { locationIn, type PathTree } from './path-location';

export interface CatalogSuggestion {
  value: string;
  label: string;
  description: string;
  category?: string;
}

/**
 * `_ref` is gone with the alias roots (`#1213`) — aliases live in the shared declaration, which the
 * engine reads too. A second alias mechanism here is what let the console affirm a path the engine
 * could not resolve.
 */
type SchemaNode = {
  _description?: string;
  /**
   * **This root is an artifact, named as an operator says it** (`#1322`) — `'demand source'`.
   *
   * On the node rather than in a list of roots, for `#1190`'s reason. What it enables is
   * {@link artifactFieldAt}: a field the artifact inherits from `ArtifactDto` is metadata about the
   * artifact, and that is declared once, on the base, in `@artifact-field-class`.
   */
  _artifact?: string;
  [key: string]: string | SchemaNode | NotOffered | ListField | undefined;
};

/**
 * **A field the model carries and the picker does not offer — with the reason on the entry**
 * (`#1217`, `X15`).
 *
 * The default is *offer it*. `X15` is ratified and narrow: *"we should only be restricting what's
 * not possible… the widest scope that meets the criteria"* (Todd) — so a field is left out only
 * when offering it would be wrong, never because nobody got round to describing it. Seven of
 * `demandSource`'s 43 were offered before this, and the missing ones were `floorCpm`, `priority`,
 * `weight`, `terms`, `enabled` — every field an operator would actually reach for.
 *
 * **It sits in the schema rather than beside it**, so there is one list and a field cannot be
 * added without deciding. That is `#1190`'s rule: an exclusion the mechanism cannot read is a
 * comment, and it survives exactly until somebody improves the mechanism.
 *
 * **A not-offered path is still a _known_ path.** {@link isKnownContextPath} answers `true` for it
 * and for everything beneath it, because the field is really there and really resolves — we have
 * only declined to enumerate it. Answering *unknown* would report our own choice as the operator's
 * mistake, which is the defect `#1213` fixed for `params`/`headers` at the root level.
 */
type NotOffered = { readonly _notOffered: string };
const notOffered = (reason: string): NotOffered => ({ _notOffered: reason });
const isNotOffered = (value: unknown): value is NotOffered =>
  typeof value === 'object' && value !== null && '_notOffered' in value;

/**
 * **A field whose value is a list — and whether its order means anything** (slice `1d`, `#1118`).
 *
 * A list's members are addressable as `genres[0]` or `genres[last]`, through the same split the
 * resolvers use (`splitAuthoredPath`), so the path check has to know which leaves are lists or it
 * warns *unknown path* on a mapping the driver resolves. It was written only as `(array)` in the
 * description text, which nothing could read.
 *
 * **`order` is the guarantee "first" rests on**, and `null` where there is none. *First genre* is the
 * publisher's primary genre because we keep the order they sent; *first tag* is whichever an operator
 * happened to type first, and offering it would present a coin flip as a decision (`#1118`'s caveat).
 * So the picker offers a list's first member only where `order` states one; `[n]` and `[last]` still
 * resolve on every list, because typing one is the operator's own claim about the order.
 */
type ListField = {
  readonly _list: true;
  readonly _description: string;
  readonly _order: string | null;
  /**
   * **What each member is, where the members are records** (`CO8`, slice `2e`, `#1369`).
   *
   * Absent on a list of values — genres, tags — which is what every list declared before `2e` was, and
   * what *a declared list holds scalars* used to assume of all of them. Present, a member is walked like
   * any node: `pod.cachedBidRefs.0.bid` and `pod.cachedBidRefs[*].bid` are known paths, where both
   * read as unknown while the list was a described leaf.
   */
  readonly _members?: SchemaNode;
};
const listOf = (description: string, order: string | null, members?: SchemaNode): ListField => ({
  _list: true,
  _description: description,
  _order: order,
  ...(members ? { _members: members } : {}),
});
const isListField = (value: unknown): value is ListField =>
  typeof value === 'object' && value !== null && '_list' in value;
/** A value the walk stops at — a described leaf or a list. */
const isLeaf = (value: unknown): value is string | ListField => typeof value === 'string' || isListField(value);
/** A segment naming one member of a list: an index, or `last`. */
const isListPosition = (segment: string): boolean => /^\d+$/.test(segment) || segment === LAST_POSITION;

/**
 * **An ad's fields as a member node, derived from their one declaration** (`CO8`, `2e`).
 *
 * `AD_TARGET_FIELDS` declares every field bidBrain carries about a returned ad, so a list of ads
 * (`collectedBids`) reads its members from there rather than from a third hand-kept copy, and a list of
 * slim references (`cachedBidRefs`) reads the subset the `ad` root's `optimizerProjection` names —
 * which `scripts/response-target-catalog.spec.ts` already holds against `BidRefDto` in both
 * directions. A field added to either declaration becomes addressable here with nobody remembering.
 */
function adMembers(only?: readonly string[]): SchemaNode {
  const node: SchemaNode = {};
  for (const field of AD_TARGET_FIELDS) {
    if (only && !only.includes(field.name)) continue;
    node[field.name.replace(/^ad\./, '')] = field.description ?? field.name;
  }
  return node;
}
const AD_MEMBERS = adMembers();
const BID_REF_MEMBERS = adMembers(INBOUND_TARGET_ROOTS.find((r) => r.name === 'ad')?.optimizerProjection?.paths ?? []);

/** The order statement shared by every list the publisher sends us. */
const PUBLISHER_ORDER = "the publisher's own order, as sent";

const DATA_MODEL_SCHEMA: SchemaNode = {
  session: {
    _description: 'Session-level metadata',
    sessionId: 'Unique session identifier',
    adUnit: "The publisher's ad unit / placement, sent as OpenRTB imp.tagid",
    cacheKey: 'Cache key reference for this session',
    strategyId: 'Request strategy ID for this session',
    requestStrategyId: 'Alias of session.strategyId (request strategy ID)',
    optimizationStrategyId: 'Optimization strategy ID for this session',
    demandSourceIds: listOf('Demand source IDs to use, if the caller specified them', null),
    podId: 'Current pod ID being processed',
    podRef: 'Cache key reference to the current pod',
    lastPodFetchTime: 'Timestamp of the last pod fetch',
    requestHeaders: 'Incoming request headers (forwarded context)',
    guidanceHints: 'Active guidance hints for decision points',
    cachedBids: 'Cached bids received for this session',
    // `#1319` item 2: the GPP pair and the transaction id, as the publisher sent them, which
    // `{gpp}`, `{gpp_sid}` and `{transaction_id}` (IAB `[GPPSTRING]`, `[GPPSECTIONID]`,
    // `[TRANSACTIONID]`) resolve from.
    regs: {
      _description: 'Privacy signals the publisher sent, kept whole',
      gpp: 'GPP string, as the publisher sent it',
      gppSid: listOf('GPP section ids that apply', PUBLISHER_ORDER),
    },
    sourceContext: {
      _description: 'The supply-chain context the publisher sent (OpenRTB source)',
      tid: "The caller's transaction id (OpenRTB source.tid)",
    },
    content: {
      _description: 'Content metadata',
      contentId: 'Unique identifier for the content',
      contentTitle: 'Title of the content',
      contentLength: 'Duration of the content in seconds',
      contentType: 'Content type: series, movie, live, short-form',
      playbackType: 'Playback type: FAST or VOD',
      genres: listOf('Genre categories for the content', PUBLISHER_ORDER),
      seriesName: 'Series name if contentType is series',
      seasonNumber: 'Season number if applicable',
      episodeNumber: 'Episode number if applicable',
      contentRating: 'Content rating (e.g., TV-PG, R, TV-MA)',
      contentUrl: 'URL of the content, for buyer contextual checks',
      contentChannel: 'Channel the content runs on, e.g. "Discovery Channel"',
      contentNetwork: 'Network the content belongs to, e.g. "Discovery"',
      iabCategories: listOf('IAB content categories', PUBLISHER_ORDER),
      language: 'Language of the content (ISO 639-1 code)',
      productionYear: notOffered(
        'nothing writes it: no inbound surface carries a production year, and OpenRTB has no field for one (#1317)',
      ),
      keywords: listOf('Keywords associated with the content', PUBLISHER_ORDER),
    },
    user: {
      _description: 'User metadata',
      userId: 'Unique user identifier (anonymized)',
      deviceType: 'Device type: CTV, mobile, tablet, desktop',
      deviceName: 'Human-readable device name (e.g., Android TV, Roku, iPhone 14)',
      idfa: 'Identifier for Advertising (IDFA/GAID)',
      ipAddress: 'IP address of the user',
      userAgent: 'User agent string from the request',
      dnt: 'Do Not Track header value',
      lmt: 'Limit ad tracking (OpenRTB 2.6 supersedes device.dnt)',
      gdpr: 'GDPR applies flag, as the publisher sent it (0/1)',
      gdprConsent: 'GDPR TC string',
      usPrivacy: 'US privacy (CCPA) string, as the publisher sent it',
      coppa: 'COPPA flag, as the publisher sent it (0/1)',
      secChUa: 'Client Hints user-agent token',
      secChUaMobile: 'Client Hints mobile indicator',
      secChUaPlatform: 'Client Hints platform token',
      timezone: notOffered(
        'nothing writes it: no inbound surface carries a time zone, and OpenRTB `geo.utcoffset` is an offset, not a zone (#1317)',
      ),
      geoLocation: {
        _description: 'Geographic location',
        country: 'Country code',
        region: 'Region/state',
        city: 'City name',
        dma: 'Designated Market Area',
        zipCode: 'Postal/ZIP code',
        latitude: 'Latitude coordinate',
        longitude: 'Longitude coordinate',
      },
      device: {
        _description: 'Device details',
        make: 'Device manufacturer',
        model: 'Device model',
        os: 'Operating system name',
        osVersion: 'Operating system version',
        hwv: 'Hardware version',
        devicetype: 'OpenRTB device type code (Table 5.21)',
        ifaType: 'Advertising identifier type (rida / idfa / gaid)',
        appVersion: 'App version',
      },
    },
    context: {
      _description: 'Session context',
      sessionStartTime: 'Timestamp when the session started',
      loopTiming: 'Loop timing override (seconds)',
      playerWidth: 'Player width in pixels',
      playerHeight: 'Player height in pixels',
      playerType: 'Player type or SDK version',
      pageUrl: 'Page URL where the player is embedded',
      appBundle: 'App bundle ID or domain',
      appName: 'App name as the publisher reports it',
      appStoreUrl: 'App store listing URL',
      appId: "The exchange's identifier for this app (not the store bundle)",
      publisherId: "The publisher's identifier on the exchange",
      publisherName: 'Publisher name',
      referrerUrl: 'The page that linked to the page the player is on (OpenRTB site.ref) — not the page itself',
      domain: "The publisher's domain (OpenRTB site.domain / app.domain)",
      muted: 'Whether player is muted',
      fullscreen: 'Whether player is fullscreen',
      connectionType: 'Network connection type',
      placement:
        'Where the ad sits relative to the content, as the caller stated it: instream, accompanying, interstitial or standalone (OpenRTB imp.video.plcmt)',
    },
  },
  pod: {
    _description: 'Pod-level data',
    podId: 'Unique pod identifier',
    podIndex: 'Position in playback (0-based)',
    sessionId: 'Session identifier',
    state: 'Lifecycle state for this pod',
    createdAt: 'Pod creation timestamp',
    updatedAt: 'Last update timestamp',
    expiresAt: 'Cache expiration time',
    fetchRound: 'Current fetch round',
    maxFetchRounds: 'Total rounds to execute',
    timing: 'Seconds between fetch rounds',
    adBreakStartTime: 'When the ad-break begins',
    adBreakDuration: 'Expected ad-break duration in seconds',
    lastAdFetchTime: 'Last ad fetch timestamp',
    nextAdFetchTime: 'Next scheduled ad fetch time',
    reqLoopStartTime: 'When req-loop should start processing',
    demandSourceIds: "IDs of the demand sources selected for this pod's fetch loop",
    /**
     * **On the req-loop path only, and that is a real asymmetry rather than a description**
     * (`#1217`). req-loop hands a driver a copy of its `PodJobDto` (`driverPodForRound`, `R-a`),
     * which carries the resolved sources — while the immediate path hands a `PodDto`, which
     * does not. So `pod.*` is two shapes depending on which path served the request.
     *
     * Offered, because `X15` restricts only what is *impossible* and this resolves on the path
     * that does most of the fetching. The description is what stops it reading as a promise.
     */
    // Its members are the `demandSource` node, attached after this literal — see below.
    demandSources: listOf(
      'Resolved demand sources for this round (req-loop path only — absent on the immediate path)',
      null,
    ),
    collectedBids: listOf(
      'Ads returned by the sources that have answered so far this round (req-loop path only)',
      null,
      AD_MEMBERS,
    ),
    roundPlans: 'Per-round request strategy execution plans',
    plannedRound: notOffered(
      "opt-engine's decision for the next round (`RQ6`); a condition reads what the last round did as `round.last.*`",
    ),
    roundPlan: notOffered('the plan a round runs, as req-loop is handed it; authored in the request-strategy editor'),
    maxAds: 'Maximum ads in pod',
    maxDuration: 'Maximum total duration in seconds',
    minimizeSlate: 'Optimize to reduce slate ads',
    curDuration: 'Current total duration',
    curValue: 'Current total value/revenue',
    cachedBidRefs: listOf('Slim bid references accumulated for this pod (not yet selected)', null, BID_REF_MEMBERS),
    curResponseRefs: listOf('Slim references for the currently selected ad set', null, BID_REF_MEMBERS),
    curResponse: 'Current optimized VAST response object',
    requestStrategyId: 'Request strategy ID selected for this pod',
    optimizerStrategy: 'Optimizer strategy selected for this pod',
    servedAt: 'Timestamp when this pod was served',
    sessionRef: 'Cache key reference to session metadata',
    sessionMetadata: 'Embedded session metadata snapshot',
    cacheKey: 'Concrete cache key for this pod object',
    requestCount: 'Total requests sent',
    bidCount: 'Total bids received',
    optimizationCount: 'Times optimization ran',
    demandSourceCallCounts: 'Per-demand-source dispatch counts',
    errors: 'Pod lifecycle error log entries',
    breakCounted: "True once this pod's ad break has been counted into the session tally",
    lastCompletedFetchRound: 'fetchRound of the last successfully processed pod.complete event',
    prefetchStopReason: 'Why the prefetch loop stopped early, if it did',

    // The request strategy in force for this pod, and the round it is on
    strategyMaxConcurrent: 'Maximum sources dispatched concurrently in one group',
    strategyFloorCpm: "The strategy's starting floor CPM",
    strategyJitterMs: 'Per-round scheduling jitter half-window, in milliseconds',
    strategyTimeoutMs: "The strategy's default per-source timeout for a round, in milliseconds",
    roundIndex: 'Index of the round being executed',
    roundFloorCpm: 'Floor CPM this round asks for',
    roundTimeout: 'Timeout this round applies, overriding the source default',
    roundSuppressedParams: listOf('Parameter names this round suppresses', null),
    roundDemandSourceId: 'The demand source this round targets, when it targets one',

    custom: notOffered(
      'the publisher pass-through bag — arbitrary keys, so it cannot be enumerated; address a key directly and it resolves',
    ),
    roundParamOverrides: notOffered('a round-plan authoring structure, authored in the request-strategy editor'),
    lastRound: notOffered('where `round.last` is stored; address it as `round.last.*` (`RT12`, `#1535`)'),
    selectionFloorCpm: notOffered(
      "the break's selection floor (`#1103`): it decides what may be selected and never what a source is asked for, and req-loop's round does not carry it, so a mapping to it would resolve to nothing where most requests are built",
    ),
  },
  /**
   * **The demand source being called** — `DemandSourceDto`, and all of it (`#1217`).
   *
   * This offered **seven** fields until 2026-09-02, against the DTO's 43. Nobody decided that;
   * seven is the set somebody typed when this file was written. Reported by Todd as *"none of the
   * demand-source fields are available in the expressions"*, and the missing ones were the whole
   * commercial half — `floorCpm`, `cpmIsFixed`, `priority`, `weight`, `terms`, `enabled`.
   *
   * `scripts/data-model-schema.spec.ts` now fails on a DTO field that is neither offered here nor
   * declared {@link notOffered} with a reason, so the next field added to the DTO cannot go missing
   * quietly. That is the **reverse** direction of the checks this frame keeps finding satisfied by
   * a shorter list.
   */
  demandSource: {
    _description: 'The demand source this request is being built for',
    _artifact: 'demand source',

    // Identity and routing
    id: 'Demand source ID',
    name: 'Demand source name',
    method: 'Protocol this source speaks — VAST, ORTB or API',
    endpoint: 'Endpoint URL the request is sent to',
    timeout: 'Local deadline for this source, in milliseconds',
    protocolVersion:
      "The protocol version this source speaks, such as 2.6 for OpenRTB or 4.0 for VAST. Absent means the protocol's default",
    enabled: 'Whether this source is active and may be selected',

    // Commercial — what we accept from it, and what we pay it
    priority: 'Selection priority (0 = highest), read by the priority-boost rule',
    // **The terms in force**, not what the document states (slice `3c-i`, `#1108`): a mapping reading
    // `demandSource.terms` gets the value its partner or Standard Terms states where the source states
    // none, so it agrees with the Terms box (`ST2`). Resolved where paths resolve, by shape.
    terms: {
      _description: "This source's contractual terms, as in force — its own, or its partner's, or Standard Terms'",
      required: 'Whether this source is required, such as a direct-sold commitment',
      cost: {
        _description: 'What we pay this source. Absent when no cost is in force',
        type: 'How the cost is stated: cpm, or revShare (a percentage of the bid)',
        value: 'A CPM amount for cpm, or a percentage of the bid for revShare (20 is 20%)',
        currency: 'The currency the cost is recorded in. Not converted',
      },
      price: {
        _description: 'What the auction uses as the gross value. Absent when no price is in force',
        type: 'floor — the least this source may be asked to bid; fixed — the agreed price',
        // **Says _floor_ whatever the type, so typing _floor_ finds it** (`#1536`, Todd 2026-10-01). A
        // fixed price is used as a floor too: sent to an auction that expects one, compared against a
        // bid, reported. The type may change, and that must not force a remapping. The search reads
        // a leaf's description, so this sentence is the alias; there is one such leaf, so no alias
        // field was built for it.
        value: "The price, as a CPM — this source's floor, or its agreed price when the price is fixed",
        currency: 'The currency the price is recorded in. Not converted',
      },
      weight: 'A boost to this source in ad selection, as a signed percent (20 is +20%)',
      maxFetchRounds: 'Maximum times req-loop will call this source per pod. Absent is no cap',
    },
    // **The ad restrictions in force** (slice `5c-i`, `#1321`) — its own, its partner's and Standard
    // Terms', tighter always winning. Replaces `pod.adRestrictions`, which had no author. What a
    // partner is sent is its own source's restrictions; the pod's selection uses every source's.
    restrictions: {
      _description: "This source's ad restrictions, as in force — its own, its partner's and Standard Terms', combined",
      minDurationSeconds: 'Shortest ad allowed, in seconds. Absent is no minimum',
      maxDurationSeconds: 'Longest ad allowed, in seconds. Absent is no maximum',
      allowIabCategories: listOf('Only these IAB categories may run. Absent allows every category', null),
      excludeIabCategories: listOf('IAB categories that may not run', null),
      excludeAdvertisers: listOf('Advertiser domains that may not run', null),
      excludeDomains: listOf('Creative domains that may not run', null),
      allowRepeatAds: 'Whether the same ad may run twice in one break. Absent is no',
    },

    // What it can be asked for
    podCapability: {
      _description: 'What this source can be asked for in one request',
      mode: "'pod' — one request may carry many impressions; 'single' — one impression per request",
      maxImpressions: 'Explicit ceiling in pod mode. Absent means no stated ceiling',
    },
    mediaType: 'Declared media type',
    creativeType: 'Declared creative type',

    // Evidence — what the product has observed, never derived (E1)
    validation: {
      _description: 'What the product has observed about this source — never derived from config',
      state: 'Observed state: proven, needsReproving, failing, unverified',
      observedAt: 'ISO timestamp of the observation that produced the state',
      lastProvenAt: 'When this source last reached proven — kept across a demotion',
      summary: 'One-line summary of the observation',
      demotedBy: 'Which request groups changed since the proof (only while needsReproving)',
    },

    // Authoring and lifecycle
    description: 'Operator description of this source',
    documentationUrl: 'Where the partner documents this integration',
    tags: listOf('Grouping tags', null),
    domains: listOf('Hostnames this template claims, for endpoint detection', null),
    status: "Lifecycle state — 'draft' awaits approval and is never selectable",
    visibility: 'Who can see this source — private, shared or public',
    isTemplate: 'Whether this artifact is a template rather than an instance',
    pixelSamplingRate: 'Fraction of pixel-received events persisted (0–1)',

    // Identity across renames and inheritance
    objectId: 'Stable identity that survives a rename of the business key',
    parentId: 'The artifact this one extends, if any',
    origin: 'Where this artifact came from — the template or parent it was created from',
    ownerId: 'The user who owns it, when a user does',
    managedBy: 'Who manages it — the system, or its owner (absent means read it from the owner)',
    createdBy: 'Who authored it — history, and never rewritten by a transfer',
    createdAt: 'When it was created',
    updatedBy: 'Who last changed it',
    updatedAt: 'When it was last changed',

    /**
     * **The exclusions, and the first one is the reason this list is declared rather than
     * implied.** `headers` carries operator-authored credentials — an `Authorization`, an API
     * key — and a mapping is an outbound surface, so offering the path is offering a way to
     * copy a secret onto a query string. Declared loudly rather than omitted quietly, which is
     * what `#1217` asked for.
     */
    headers: notOffered(
      'carries authored credentials — a mapping is an outbound surface, so a secret must not be addressable from one',
    ),
    params: notOffered('an authoring structure with its own editor — author the row there, where the wire rules apply'),
    headersProvenance: notOffered('authoring metadata about who set each header, not a value to send'),
    partnerId: notOffered(
      'names the commercial counterparty this source buys under — a relationship between two artifacts, and our internal key for one, which no receiver has a use for',
    ),
    resolvedTerms: notOffered(
      'the same terms under a second spelling, resolved across the tiers — demandSource.terms is the one address, and making it resolve to the value in force is slice 3c, where the Terms box lands',
    ),
    removedFromParent: notOffered(
      'names the parent fields this source does not send (`#1126`) — authoring structure, and every one of them is absent from the request by definition',
    ),
    authoring: notOffered(
      'authoring metadata — who created and last changed this, already offered flat as createdBy/updatedBy',
    ),
    responseMapping: notOffered('a mapping document with its own editor'),
    ortbOutboundMapping: notOffered('a mapping document with its own editor'),
    ortbBodyTemplate: notOffered(
      'the ORTB body template itself — editing it from inside one of its own values is a cycle',
    ),
    prebidConfig: notOffered('Prebid Server adapter configuration, authored in its own section'),
    customPixels: notOffered('pixel configurations with their own editor'),
    trackingPixels: notOffered(
      'superseded by customPixels; kept on the DTO because drivers still read it for seeded sources',
    ),
  },
  /**
   * **The open roots** (`XS2a`, `#1213`). They resolve on the engine and were offered by nothing,
   * because a schema can only offer what it can enumerate and these hold whatever the caller sent.
   * They are roots with no children here on purpose — {@link isOpenContextRoot} accepts any path
   * beneath them.
   */
  params: { _description: CONTEXT_ROOTS.find((r) => r.name === 'params')?.description },
  headers: { _description: CONTEXT_ROOTS.find((r) => r.name === 'headers')?.description },
  response: { _description: CONTEXT_ROOTS.find((r) => r.name === 'response')?.description },
  round: {
    _description: CONTEXT_ROOTS.find((r) => r.name === 'round')?.description,
    number: 'The round being planned, counting from 1',
    last: {
      _description: 'What the previous round did (`RT12`) — absent on the first round',
      roundIndex: 'Which round it was, counting from 0',
      pod: {
        _description: 'The pod as selected after it',
        adCount: 'Ads selected',
        durationSeconds: 'Seconds selected',
        fill: 'Seconds selected ÷ the break, from 0 to 1',
        value: 'The pod value',
        avgCpm: 'Average CPM of the selected ads, leaving out required sources — absent when none are left',
        cpmPerSecond: 'The pod value ÷ seconds selected — absent when none are',
      },
      overall: {
        _description: 'Every source together, in the previous round',
        roundIndex: 'Which round it was, counting from 0',
        sourcesCalled: 'Sources called',
        sourcesResponded: 'Sources that returned an ad',
        requests: 'Requests sent',
        adsReturned: 'Ads returned',
        adSecondsReturned: 'Seconds of ads returned',
        avgCpm: 'Average CPM of what came back, leaving out required sources — absent when none is left',
        maxCpm: 'Highest CPM that came back, leaving out required sources — absent when none is left',
        cpmPerSecond:
          'Total value ÷ total seconds — compares ads of different lengths; absent when no seconds came back',
        value: 'Sum of the CPMs that came back',
        adsSelected: 'How many of its ads the selection included',
        winRate: 'Ads selected ÷ ads returned — absent when nothing came back',
      },
      source: {
        _description: 'The source this override is for, in the last round this break called it — absent until one has',
        roundIndex: 'Which round that was, counting from 0',
        requests: 'Requests sent',
        adsReturned: 'Ads returned',
        adSecondsReturned: 'Seconds of ads returned',
        avgCpm: 'Average CPM of what came back — absent when nothing did',
        maxCpm: 'Highest CPM that came back — absent when nothing did',
        cpmPerSecond:
          'Total value ÷ total seconds — compares ads of different lengths; absent when no seconds came back',
        value: 'Sum of the CPMs that came back',
        adsSelected: 'How many of its ads the selection included',
        winRate: 'Ads selected ÷ ads returned — absent when nothing came back',
      },
    },
  },
};

/**
 * **A round's demand sources are demand sources**, so each member is the `demandSource` node — the same
 * fields, the same `notOffered` declines (`headers` among them), one declaration. Attached here because
 * an object literal cannot name its own sibling; a copy of the node would be the second declaration
 * `#1313`'s credential guard would then have to be kept in step with.
 */
{
  const pod = DATA_MODEL_SCHEMA.pod as SchemaNode;
  const list = pod.demandSources as ListField;
  pod.demandSources = { ...list, _members: DATA_MODEL_SCHEMA.demandSource as SchemaNode };
}

/**
 * **The alias roots are gone, and that is the fix rather than a tidy** (`#1213`).
 *
 * `content`, `user` and `context` used to sit here as `_ref` nodes, which made
 * `isKnownContextPath('user.deviceType')` return **true** while `resolveMacroPath` returned
 * `undefined` — the console affirming a path that sends nothing, so the surface read as proof
 * (`#1002`). The spellings still work, because they are **input spellings**: they are canonicalised
 * through the shared declaration on the way in, so what is stored is `session.user.deviceType` and
 * the affirmation is now true.
 */

/**
 * **The matcher's vocabulary, derived from the catalog** (#1190) — every spelling that may
 * be inferred from a name we did not author, mapped to the concept it names.
 *
 * ## What this replaced, and why it was not a tidy-up
 *
 * This was a **hand-kept `Record<string, string>` of 71 spellings** sitting beside
 * `catalogPathsByName()` — a derivation written for exactly this job, described in its own
 * docstring as *"the matcher's wide vocabulary"*, with **zero callers repo-wide**. So the
 * derived list existed, the hand-kept one was wired, and the hand-kept one drifted. That is
 * #1189: eleven concepts the catalog declared were unreachable here, and the one that was
 * reported sent a pod id as the viewer's advertising id.
 *
 * Measured before the swap, 110 catalog spellings against 71: **46 gained, 0 disagreeing on
 * a path.** There was never a conflict to adjudicate — only coverage, hand-kept.
 *
 * ## The seven that looked like losses, and are not
 *
 * Seven camelCase spellings lived here and not in the catalog (`ipAddress`, `userAgent`,
 * `deviceType`, `deviceName`, `contentRating`, `connectionType`, `appStoreUrl`). Every one
 * survives the swap through the **token-run rule** rather than through the vocabulary:
 * `tokenize` splits camelCase, so `ipAddress` → `[ip, address]` matches the catalog's
 * `ip_address` exactly. They stop being separate vocabulary entries and keep matching, which
 * is the one-name-per-concept rule getting what it wanted for free. `resolvable-macros.test.ts`
 * asserts each of the seven rather than trusting the reasoning.
 *
 * ## Keyed on the **concept**, which is the shape change
 *
 * A name→**path** map could not reach a value bidBrain *produces* (`cb`, `timestamp`,
 * `device_type_code`) or resolves across a *chain* with no primary path (`schain`, `domain`)
 * — five concepts unreachable by construction rather than by decision, so a pasted `cb=1234`
 * matched nothing at all. `cb` is the sharp one: on the VAST path a stored `cb` row is
 * rescued by `resolveParamFieldValue`'s name-based fallback, so nothing ever reported the
 * hole; an ORTB body has no such rescuer. Keying on the entry makes the path what a match
 * *carries* rather than what it is looked up by, and the five become reachable.
 *
 * ## The three declines it must not reverse
 *
 * `bundle`, and every spelling of `max_duration` and `pod_ads`, are deliberately not
 * inferable — and a straight swap would have restored all three **silently**, passing every
 * gate, because the reach check is keyed on *concept reachable by at least one spelling* and
 * a wider vocabulary satisfies it more thoroughly. Two of the three lived in a spec file and
 * the third in a comment on this very table, so the matcher could read none of them. They are
 * `FieldCatalogEntry.neverInferred` now, which is the only place the derivation can honour.
 *
 * ## The path each spelling carries is still the *calculated* path, and that still matters
 *
 * A `calculated` expression resolves by literally walking the path (`_resolvePath` in
 * `macro-expression.engine.ts`); it does **not** go through the multi-location
 * `resolveContextValue`. The hand-kept table carried that warning because it had once been
 * wrong — `session.device.*`, `session.video.*` and `session.privacy.*` matched no node in
 * `SessionMetadataDto`, so every auto-attached mapping resolved to its fallback and shipped
 * empty or a literal `{token}` (#882). Deriving from the catalog is what makes that
 * unrepeatable rather than re-checked: `field-catalog.spec.ts` roots every declared path at a
 * context the resolver exposes, so the paths cannot drift from the session shape here again.
 */
export const MACRO_VOCABULARY: ReadonlyMap<string, CatalogConcept> = inferableSpellings();

/**
 * The macro names the free-text type-down offers, **built from the catalog** (`PF`, D20).
 *
 * **Curated here, described there.** Which names to surface in a type-down is a UI
 * decision, so the list stays local; the *spelling* and the *description* come from
 * `FIELD_CATALOG`, so a token cannot be offered that nothing resolves and two lists cannot
 * describe one concept differently.
 *
 * **It is a separate question from `SUGGESTED_MACRO_NAMES`, and deliberately so.** That
 * list answers *what to write into a row when seeding it from a parameter name* — a
 * mapping proposal. This one answers *what to show someone typing a macro by hand*. They
 * legitimately differ: a type-down answers a name the operator is already typing, while a
 * paste has to decide what somebody else's name meant. Same shape as `MACRO_DEFAULTS` versus
 * the suggested set, and the same reason to keep them apart.
 *
 * **This read *"auto-mapping a pasted parameter to the session id never is [reasonable]"* until
 * #1189, and that sentence was wrong by the time it was quoted.** `session_id` and `pod_id` are
 * `perRequest` in the catalog (#1188), which means the literal must be discarded and the field
 * mapped to its macro — so declining to match them does not leave the row alone, it leaves a
 * stranger's session id frozen in the configuration, or hands the field to the value heuristic.
 * A statement that is true of one list is not automatically true of the other; this one was
 * about what to *suggest* and got read as what to *match*.
 *
 * **What they may not do is contradict each other**, which they did. This list sat eighty
 * lines below `MACRO_DEFAULTS`, was in no comparison at all (§6a), and offered `{dur}`
 * where the picker suggests `content_length` — the exact ambiguity behind #1014 — plus
 * camelCase `{deviceType}`/`{deviceName}` against a vocabulary that decided on snake_case.
 * `resolvable-macros.test.ts` now asserts an offered name is the catalog's preferred
 * spelling for its concept.
 */
const MACRO_HELPER_NAMES: readonly string[] = [
  'session_id',
  'pod_id',
  'pod_index',
  'ip',
  'ua',
  'device_type',
  'device_name',
  'gdpr',
  'gdpr_consent',
  'us_privacy',
  'dnt',
  'lmt',
  'w',
  'h',
  'url',
  'content_length',
  'cb',
  'schain',
  // Concepts with no session path to seed a row from, offered to whoever types them (found
  // rehearsing `#1319`: `{domain}` resolved and was offered nowhere). A path-less or produced value
  // cannot be a `suggest`, because that list seeds a row with a path. When one gains a path — a
  // global field, or a template custom field (`#1205`) — it moves to `suggest`, and
  // `resolvable-macros.test.ts` fails until it does.
  'domain',
  'lat_long',
  'player_size',
  'regulations',
  'server_side',
  'device_type_code',
  // This source's own restrictions (`1f-ii`). Never inferred from a paste, so offered only here.
  'min_duration',
  'max_duration',
  'blocked_ad_categories',
  'allowed_ad_categories',
];

export const MACRO_HELPER_SUGGESTIONS: CatalogSuggestion[] = MACRO_HELPER_NAMES.map((name) => {
  const entry = catalogEntryFor(name);
  return {
    value: `{${name}}`,
    // The description, not the value again — a `<datalist>` renders both, so a label equal to the
    // value prints the name twice and teaches nothing (`#1259`, applied to an ordinary row).
    label: entry?.description ?? `{${name}}`,
    // The catalog's own words, so the type-down and the picker cannot describe one concept
    // two ways — which is how `{dur}` came to be called a duration in one place and content
    // length in the other.
    description: entry?.description ?? name,
    category: 'macro' as const,
  };
});

/**
 * **Spellings the model does not carry, offered only to whoever types them** (`#1213`, `#1249`).
 *
 * `genre` and `rating` are what an operator reaches for; the model's children are `genres` and
 * `contentRating`. They are *input spellings*, not members of `session.content` — so they are
 * offered by the **search** leg, where somebody has typed the word and the offer is the help they
 * asked for, and withheld from the **walk**, which enumerates the model and would otherwise show
 * both spellings of one field side by side.
 *
 * **Exported because that makes the exception readable by the gate that would otherwise report it**
 * (`#1190`): *walking reaches everything searching can find* is true of the model and false of
 * these two, and a decline the mechanism cannot read is a comment rather than a decline.
 */
export const ALIAS_PATH_SUGGESTIONS: CatalogSuggestion[] = [
  {
    value: 'session.content.genre',
    label: 'session.content.genre',
    description: 'Alias for session.content.genres',
    category: 'content',
  },
  {
    value: 'session.content.rating',
    label: 'session.content.rating',
    description: 'Alias for session.content.contentRating',
    category: 'content',
  },
];

function normalizeSearchToken(input: string): string {
  return input.toLowerCase().replace(/[\s_-]/g, '');
}

function getAllFieldPaths(): string[] {
  const paths: string[] = [];

  function traverse(node: SchemaNode, prefix = '') {
    Object.entries(node).forEach(([key, value]) => {
      if (key.startsWith('_') || value === undefined) return;
      // A declared exclusion is a decision *not* to offer, so it never reaches the picker —
      // and it is still known (see `isKnownContextPath`), because the field is really there.
      if (isNotOffered(value)) return;
      const path = prefix ? `${prefix}.${key}` : key;
      if (isLeaf(value)) {
        paths.push(path);
        return;
      }
      paths.push(path);
      traverse(value, path);
    });
  }

  traverse(DATA_MODEL_SCHEMA);
  return Array.from(new Set(paths)).sort((a, b) => a.localeCompare(b));
}

function resolveSchemaPath(path: string): string {
  const parts = path.split('.');
  let current: SchemaNode | string | NotOffered | ListField | undefined = DATA_MODEL_SCHEMA;

  for (const part of parts) {
    if (!current || isLeaf(current) || isNotOffered(current)) return '';
    current = current[part];
  }

  if (typeof current === 'string') return current;
  if (isNotOffered(current)) return '';
  return current?._description ?? '';
}

/**
 * Does this path name a node the session/pod model actually has? (#998)
 *
 * A `calculated` mapping resolves by **literally walking the path**, so a path
 * matching no node resolves to its fallback on every request — silently, and
 * invisibly when a `{macro}` beside it supplies the value anyway. This is what
 * makes such a mapping detectable at authoring time, which is the whole ask.
 *
 * The schema was itself the cause worth fixing first: it declared
 * `session.privacy.*`, `session.device.*` and `session.video.*`, so the
 * autocomplete **offered** three branches `SessionMetadataDto` has never had.
 * An operator picking a dead path was picking what the product suggested, and no
 * warning would have been honest while that was true. Those are gone; the fields
 * they named live on `session.user.*` and `session.context.*`.
 *
 * Canonicalises first, so `user.deviceType` and `session.user.deviceType` are both known — and the
 * first is stored as the second, which is what makes the affirmation true (`#1213`).
 */
export function isKnownContextPath(path: string): boolean {
  /**
   * **`list[*].member` is known where the list is and its members carry that field** (`CO8`, `2e`) —
   * the same walk as `list.0.member`, since *each* and *one* name the same member node. Split here
   * because `splitAuthoredPath` deliberately does not know `[*]` (see `EACH_POSITION`).
   */
  const each = splitAtEachPosition(path.trim());
  if (each) {
    const list = listFieldAt(canonicalizeContextPath(splitAuthoredPath(each.path).join('.')));
    if (!list) return false;
    // A list of records with no member named folds nothing — each member is an object, not a value.
    if (!each.member) return !list._members;
    return list._members !== undefined && isKnownBeneath(list._members, splitAuthoredPath(each.member));
  }
  // An alias is an input spelling (`#1213`), so it is resolved through the shared declaration
  // before anything is judged — one alias mechanism, not a second one living in this file.
  // `genres[0]` is `genres.0` (`#1118`) — split through the resolvers' own rule before the alias is
  // read, so `session.content.genre[0]` canonicalises like its dotted spelling does.
  const parts = splitAuthoredPath(canonicalizeContextPath(splitAuthoredPath(path).join('.')));
  if (parts.length === 0) return false;

  // An open root holds arbitrary keys, so any path beneath it is valid. Walking the schema for
  // `params.pubid` and answering *unknown* reports our ignorance as the operator's mistake.
  if (isOpenContextRoot(parts[0])) return true;

  return isKnownBeneath(DATA_MODEL_SCHEMA, parts);
}

/**
 * **What is wrong with a `list[*].member`, if anything** (`CO8`, `2e`) — the reason behind
 * {@link isKnownContextPath}'s `false`, so the finding can say which of three things it is. The
 * suggested member is the list's first declared one, so the example is a real path.
 */
/**
 * **The rows a slot that folds a list adds to the type-down** (`CO8`, `2e`) — Todd: *"`sum(array)`…
 * should work, and the UI should allow it."* Only a calculation's operand asks for these, so the
 * ordinary type-down never offers `[*]` where it would resolve to nothing.
 *
 * - Typing up to a list offers `list[*]` beside it — every member, where the list holds values, or the
 *   list's records to name a field of.
 * - Typing past `[*]` offers the members' fields, as `list[*].field`.
 */
export function eachMemberSuggestions<
  S extends { value: string; label?: string; description?: string; category?: string },
>(query: string, offered: readonly S[]): (S | CatalogSuggestion)[] {
  const each = splitAtEachPosition(query.trim());
  if (each) {
    const list = listFieldAt(canonicalizeContextPath(splitAuthoredPath(each.path).join('.')));
    if (!list?._members) return [];
    const typed = (each.member ?? '').toLowerCase();
    return Object.entries(list._members)
      .filter(([key, node]) => !key.startsWith('_') && node !== undefined && !isNotOffered(node))
      .filter(([key]) => key.toLowerCase().startsWith(typed))
      .map(([key, node]) => ({
        value: `${each.path}[*].${key}`,
        label: typeof node === 'string' ? node : ((node as { _description?: string })._description ?? key),
        description: `The ${key} of each`,
        category: each.path.split('.')[0],
      }));
  }
  return offered.flatMap((suggestion): (S | CatalogSuggestion)[] => {
    const list = listFieldAt(suggestion.value);
    if (!list) return [suggestion];
    return [
      suggestion,
      {
        value: `${suggestion.value}[*]`,
        label: `${suggestion.value}[*]`,
        description: list._members ? 'Each one — then name the field to read' : 'Every member, folded together',
        category: suggestion.category ?? suggestion.value.split('.')[0],
      },
    ];
  });
}

export type EachPathProblem =
  | { kind: 'not-a-list' }
  | { kind: 'needs-member'; example: string }
  | { kind: 'unknown-member' };

export function eachPathProblem(each: { path: string; member?: string }): EachPathProblem | null {
  const list = listFieldAt(canonicalizeContextPath(splitAuthoredPath(each.path).join('.')));
  if (!list) return { kind: 'not-a-list' };
  if (!each.member) {
    if (!list._members) return null;
    const first = Object.keys(list._members).find((key) => !key.startsWith('_'));
    return { kind: 'needs-member', example: first ?? 'field' };
  }
  return list._members && isKnownBeneath(list._members, splitAuthoredPath(each.member))
    ? null
    : { kind: 'unknown-member' };
}

/** The walk {@link isKnownContextPath} takes, from any node — the schema's root or a list's members. */
function isKnownBeneath(start: SchemaNode, parts: readonly string[]): boolean {
  let current: SchemaNode | string | NotOffered | ListField | undefined = start;
  for (let i = 0; i < parts.length; i += 1) {
    if (isListField(current)) {
      // One member of a list — an index or `last`. A list of values ends there (`genres.0.x` is as
      // unknown as it was); a list of records continues into what each member is.
      if (!isListPosition(parts[i])) return false;
      if (i === parts.length - 1) return true;
      if (!current._members) return false;
      current = current._members;
      continue;
    }
    if (current === undefined || typeof current === 'string') return false;
    // A field we declined to *offer* is still a field the model has, and so is everything
    // beneath it — we simply stopped enumerating. Answering *unknown* here would report our
    // own decision as the operator's mistake, which is `#1213` one level down.
    if (isNotOffered(current)) return true;
    current = current[parts[i]];
  }
  return current !== undefined;
}

/** A path that addresses metadata about an artifact rather than content of it (`#1322`). */
export interface ArtifactFieldAt {
  /** The path in its canonical spelling. */
  path: string;
  /** The artifact, as an operator names it — `'demand source'`. */
  artifact: string;
  declaration: ArtifactFieldDeclaration;
}

/**
 * **Is this path metadata about an artifact?** — and which kind (`#1322`).
 *
 * `null` when the root is not an artifact, or the field is the artifact's own. Anything beneath a
 * classified field is classified with it: `demandSource.validation.state` is evidence because
 * `validation` is.
 */
export function artifactFieldAt(path: string): ArtifactFieldAt | null {
  const parts = canonicalizeContextPath(path).split('.').filter(Boolean);
  if (parts.length < 2) return null;
  const root = DATA_MODEL_SCHEMA[parts[0]];
  if (!root || isLeaf(root) || isNotOffered(root) || !root._artifact) return null;
  const declaration = artifactFieldDeclaration(parts[1]);
  return declaration ? { path: parts.join('.'), artifact: root._artifact, declaration } : null;
}

const CATALOG_PATHS = getAllFieldPaths();

/**
 * Paths reachable by typing a **macro name** into the path type-down.
 *
 * The two sides of a mapping are two names for one concept, and guidance §3 says both
 * autocomplete — but the path search only ever matched the path text and its
 * description, so a macro name that is not a substring of its own path found nothing.
 * Four default-suggested macros were unreachable that way, from two causes:
 *
 * | Macro | Path | Why the text search missed it |
 * |---|---|---|
 * | `ua` | `session.user.userAgent` | `ua` does not occur in `useragent` |
 * | `ifa` | `session.user.idfa` | nor in `idfa` — `i-d-f-a` has no `ifa` |
 * | `pod_max_duration` | `pod.maxDuration` | the `.` is not stripped, so `podmaxduration` never spans it |
 * | `pod_max_ads` | `pod.maxAds` | same |
 *
 * Reported by Todd for `ifa` specifically, which the previous change had just made the
 * **default** macro — so the name the editor now recommends was the one its sibling
 * control could not find.
 *
 * **Prefix, not substring** — the macro vocabulary is closed and small, so a prefix match
 * is precise enough to hoist a concept without the noise a substring rule would produce
 * from one- and two-letter names (`w`, `h`, `ip`). That is the #1003 lesson applied to
 * search rather than to mapping.
 *
 * **And an exact name beats a prefix**, the same precedence `matchMacroName` uses. Without
 * it, typing `ifa` led with `session.user.device.ifaType`, because `ifa_type` sorts before
 * `ifa` in the object and prefix-matches it — the exact name the operator typed losing to
 * a longer one that merely starts the same way.
 */
function pathsForMacroQuery(normalizedQuery: string): string[] {
  if (!normalizedQuery) return [];
  const exact: string[] = [];
  const prefixed: string[] = [];
  for (const [macro, entry] of MACRO_VOCABULARY) {
    // A path search, so a produced or chained concept legitimately has nothing to offer it.
    if (!entry.path) continue;
    const normalized = normalizeSearchToken(macro);
    if (normalized === normalizedQuery) exact.push(entry.path);
    else if (normalized.startsWith(normalizedQuery)) prefixed.push(entry.path);
  }
  return [...new Set([...exact, ...prefixed])].filter((path) => CATALOG_PATHS.includes(path));
}

/**
 * **Every macro the editor suggests, as a type-down entry** (`XS3` slice 3, #1222).
 *
 * This was `MappingControl`'s own `MACRO_SUGGESTIONS`, built for the **Macro tab's** picker. `X27`
 * removes that tab — a macro is sugar over a path and is typed into the one value field — so the
 * list had to move rather than be deleted with the control that read it. Deleting it would have
 * narrowed what is offered from {@link SUGGESTED_MACRO_NAMES} down to
 * {@link MACRO_HELPER_SUGGESTIONS}' eighteen, which is *a gate satisfied by doing less* (#1189)
 * arriving through a component being retired: no assertion fails when an offer list gets shorter.
 *
 * It lives here rather than in the value line because this is where the vocabulary lives, and
 * because `resolvable-macros.test.ts` reads {@link SUGGESTED_MACRO_NAMES} directly — the offered
 * *set* is unchanged by the move, which is what keeps that gate meaningful.
 *
 * **Built on first use rather than at module load**, because `SUGGESTED_MACRO_NAMES` is declared
 * below it and a module-level `const` here reads it in its temporal dead zone — which is not a type
 * error and fails only when something imports this module.
 */
let suggestedMacroSuggestions: CatalogSuggestion[] | undefined;

export function getSuggestedMacroSuggestions(): CatalogSuggestion[] {
  suggestedMacroSuggestions ??= [...MACRO_VOCABULARY]
    .filter(([name]) => SUGGESTED_MACRO_NAMES.has(name))
    .map(([name, concept]) => ({
      value: `{${name}}`,
      // What it resolves to is the fact worth showing beside the spelling (`#1259`).
      label: concept.path ?? concept.description,
      description: concept.path ?? concept.description,
      category: 'macro',
    }));
  return suggestedMacroSuggestions;
}

/**
 * **What the one value field offers** — paths and macros together (`X23`/`X27`, `XS3` slice 3).
 *
 * The value line accepts a literal, a path or a `{macro}` in one box, so its type-down has to offer
 * both halves or removing the Macro tab makes half the vocabulary undiscoverable — *"removing a
 * picker is only an improvement if what it advertised is still discoverable"* (`X28`).
 *
 * The two macro lists are deliberately different questions ({@link MACRO_HELPER_SUGGESTIONS} answers
 * *what to show someone typing by hand*, {@link SUGGESTED_MACRO_NAMES} answers *what to write into a
 * row when seeding it*), and both were reachable from an authoring surface before this. The union is
 * the only direction that loses nothing, and it is deduplicated by spelling so a name in both lists
 * appears once.
 */
export function getValueSuggestions(query: string): CatalogSuggestion[] {
  const schema = getSchemaSuggestions(query);
  const seen = new Set(schema.map((s) => s.value));
  const normalizedQuery = normalizeSearchToken(query.trim());
  // **Macros belong to the search, not to the walk** (`#1249`, Todd 2026-09-11).
  //
  // Two surfaces open this list on focus (`minChars={0}`), where it was 100 rows: 52 paths and 48
  // macros. `{` is the macro half's gesture exactly as `.` is the path half's — every macro is
  // spelled `{name}`, so typing `{` lists all of them and typing `ip` finds `{ip}`. What is
  // withheld is the *unasked* list, never a name (`#1189`: an offer list that silently gets
  // shorter fails no assertion, so the gesture that restores it is asserted).
  //
  // A **location** query withholds them for a second reason: an operator typing `session.content`
  // is asking what is inside it, and every macro whose path starts with that prefix matches the
  // text — so the walk would answer *what is here* with forty spellings of things that are here.
  if (!normalizedQuery || readsAsLocation(query)) return schema;
  const macros = getSuggestedMacroSuggestions().filter(
    (item) =>
      !seen.has(item.value) &&
      (normalizeSearchToken(item.value).includes(normalizedQuery) ||
        normalizeSearchToken(item.description).includes(normalizedQuery)),
  );
  return [...schema, ...macros];
}

/**
 * **The object model, marked for the surface asking — never narrowed** (`X32` Q1, `#1248`).
 *
 * This was `getSchemaSuggestionsForRoots`, which **filtered** the roots a surface does not set. That
 * was wrong, and `X32` had already ruled the other way:
 *
 * > *"A path that cannot resolve at this phase is **offered and marked, not hidden**: hiding it
 * > answers by silence, and the operator's next move is to type it by hand and get nothing."*
 *
 * The reasoning behind the filter was `#977`'s family — *offered and resolving to nothing* — and it
 * does not apply. `#977` is a name that resolves **nowhere, ever**, which is a vocabulary defect.
 * `params.*` on the response surface resolves on three other surfaces and is merely **not knowable at
 * this phase**, which is a property of the phase. The operator needs to be told which, and only one
 * of the two is a reason to remove a name.
 *
 * ## One mark per claim, at the level the claim is made
 *
 * Where a **whole root** is unknowable there is one fact, not forty — so the root collapses to a
 * single marked row rather than repeating itself down every child (Todd, 2026-09-04). It is also the
 * only shape that works for an open root, which has no children to enumerate.
 *
 * ## The mark is text, because the type-down is a native `<datalist>`
 *
 * An `<option>` renders its `value` and its text content, and nothing else — no styling, no second
 * line. So `label` carries the mark and `value` stays the path that gets inserted. Recorded because
 * it is a constraint on the design rather than a choice within it.
 *
 * **`#1249` did not bring the custom listbox this once pointed at, and decided not to.** Making the
 * list a walk rather than a flat search removed the density problem the listbox was wanted for —
 * no level of the model is long enough to need styling to be readable — so the honest form is
 * still a sentence, now by decision rather than by deferral. A styled mark remains unbuilt and
 * unscheduled; say so rather than naming a control that is coming (`#1045`).
 */
export function getSchemaSuggestionsForSurface(query: string, builderKey: string): CatalogSuggestion[] {
  return markForSurface(
    getSchemaSuggestions(query).filter((s) => !s.value.startsWith('{')),
    query,
    builderKey,
  );
}

/**
 * **The value type-down, marked for the surface asking** (`#1524`). The outbound editors took
 * {@link getValueSuggestions} unmarked, so a surface that could not resolve a path said nothing about
 * it. Macros pass through untouched: a `{name}` is not a path under a root.
 */
export function getValueSuggestionsForSurface(query: string, builderKey: string): CatalogSuggestion[] {
  return markForSurface(getValueSuggestions(query), query, builderKey);
}

/**
 * The marking both surface lists share. A root the surface does not set collapses to one marked row;
 * a path under a root it does set is marked on its own where the surface cannot resolve it — today
 * `session.` and `pod.`, whose driver projections are narrower than what opt-engine holds (`#1524`, `R-c`).
 */
function markForSurface(suggestions: CatalogSuggestion[], query: string, builderKey: string): CatalogSuggestion[] {
  const canonical = canonicalizeContextPath(query.trim());
  const out: CatalogSuggestion[] = [];
  const collapsed = new Set<string>();

  for (const suggestion of suggestions) {
    if (suggestion.value.startsWith('{')) {
      out.push(suggestion);
      continue;
    }
    const root = suggestion.value.split('.')[0];
    // `undefined` is *nobody declared this surface*, which must not read as *nothing resolves here*.
    // An undeclared surface marks nothing rather than marking everything (`runtimeUndecided`'s rule).
    if (knowableAt(builderKey, root) !== false) {
      out.push(
        knowableAt(builderKey, suggestion.value) === false
          ? // A site in opt-engine is handed nothing; it does not hold the path where it decides.
            markSuggestion(suggestion, [
              RESOLUTION_SITES[builderKey]?.tier === 'opt-engine' ? NOT_KNOWABLE_HERE : NOT_HANDED_TO_A_SOURCE,
            ])
          : suggestion,
      );
      continue;
    }
    if (collapsed.has(root)) continue;
    collapsed.add(root);
    const typedInto = canonical === root || canonical.startsWith(`${root}.`);
    out.push(
      markSuggestion(
        {
          value: typedInto ? canonical : root,
          label: root,
          description: CONTEXT_ROOTS.find((r) => r.name === root)?.description ?? '',
          category: root,
        },
        [NOT_KNOWABLE_HERE],
      ),
    );
  }
  return out;
}

/**
 * **A mark on a suggestion replaces the label, it does not extend it** (`#1259`, Todd 2026-09-08).
 *
 * A native `<datalist>` option renders its `value` **and** its text content, one under the other, so
 * a label built as `` `${path} — ${mark}` `` shows the path twice:
 *
 * > *"the typedown list show 'field name - served, not used…' immediately below the field name. We
 * > don't need `field name - `."*
 *
 * The value is already on screen and the mark is the only new fact, so the mark is the whole label.
 * Shared because both marked type-downs — the read side's collapsed root here, the store side's
 * grain in `response-field-catalog.ts` — had composed that label themselves, and *shared logic is
 * not shared words*: two surfaces deciding one rendering question separately is how they drift.
 *
 * Marks join with `·` and an empty set leaves the suggestion alone, so *nothing to say* renders as
 * the plain row rather than as an empty second line.
 */
export function markSuggestion<T extends { value: string; label?: string }>(
  suggestion: T,
  marks: ReadonlyArray<string | undefined>,
): T {
  const text = marks.filter(Boolean).join(' · ');
  return text ? { ...suggestion, label: text } : suggestion;
}

/**
 * The sentence a not-knowable-here mark carries, shared so two surfaces cannot come to say different
 * things about one fact (`#1187`). It states the **consequence**, not the mechanism (`#1045`): an
 * operator does not need to know which builder sets which root, they need to know it will be empty.
 */
export const NOT_KNOWABLE_HERE = 'not known at this point, so it resolves to nothing here';

/**
 * The mark on a `session.` or `pod.` path a demand source's surface cannot resolve (`#1524`, `RT13`,
 * `R-c`): the source is handed a projection of each, and this is not in it. Stated as the consequence,
 * like {@link NOT_KNOWABLE_HERE}; the pod's own spelling of a session value, where there is one, is
 * offered beside it.
 */
export const NOT_HANDED_TO_A_SOURCE = 'not handed to a demand source, so it resolves to nothing here';

/**
 * **What an open root offers instead of children** (`#1249`).
 *
 * `params`, `headers` and `response` hold whatever the caller sent, so descending into one has
 * nothing to enumerate — and an empty list reads as *this is broken* rather than as *this is
 * yours to name*. `X15`'s rule is that *we cannot list your keys* is unknown to us, not
 * impossible, so the row states the consequence and names the act (`#1045`).
 *
 * Shared rather than written per root, because three surfaces saying one fact three ways is how
 * they drift (`#1187`).
 */
export const OPEN_ROOT_HAS_NO_LIST = 'holds whatever the caller sent, so we cannot list it — type the name you expect';

/**
 * The children a branch declares, or `null` where the path is not a branch at all.
 *
 * An empty array and `null` are different answers and the caller depends on the difference: an
 * open root **is** a branch with nothing to enumerate, where `sess` is not a branch and should
 * fall back to searching.
 */
function childSegmentsOf(base: string): string[] | null {
  let current: SchemaNode | string | NotOffered | ListField | undefined = DATA_MODEL_SCHEMA;
  for (const part of base.split('.').filter(Boolean)) {
    if (!current || isLeaf(current) || isNotOffered(current)) return null;
    current = current[part];
  }
  if (!current || isLeaf(current) || isNotOffered(current)) return null;
  return Object.entries(current)
    .filter(([key, value]) => !key.startsWith('_') && value !== undefined && !isNotOffered(value))
    .map(([key]) => key);
}

/**
 * One offered row, with **the description as its label** (`#1259`).
 *
 * A native `<datalist>` renders an option's `value` and its text content one under the other, so a
 * label equal to the value prints the path twice and teaches nothing — which is exactly what
 * `#1259` ruled against for marks, arriving here at every ordinary row. The value is already on
 * screen; the description is the only new fact, so it is the label.
 *
 * A **branch** says how many fields are inside it, because that is the fact that differs per row
 * and it is what tells an operator the row is worth opening (`#1248`: state a claim at the level
 * it is true at). It is not *type a dot to descend* on every row — selecting a branch descends on
 * its own, so the gesture teaches itself and repeating it twenty-two times would bury the count.
 */
/** The list declared at this path, or `null` where the path is not a list. */
function listFieldAt(path: string): ListField | null {
  let current: SchemaNode | string | NotOffered | ListField | undefined = DATA_MODEL_SCHEMA;
  for (const part of path.split('.').filter(Boolean)) {
    if (!current || isLeaf(current) || isNotOffered(current)) return null;
    current = current[part];
  }
  return isListField(current) ? current : null;
}

/**
 * **A path, and — where it is a list whose order means something — its first member** (`#1118`).
 *
 * The capability existed on both protocols for weeks and nothing offered it: `genres.0` resolved and
 * no picker said so. The row sits directly under its list, so it is found where the list is. It is
 * offered only where the list declares its order, and it says what the order is, because *first*
 * is a claim about the order and the operator should see whose claim it is.
 */
function suggestionsForPath(path: string): CatalogSuggestion[] {
  const own = suggestionForPath(path);
  const order = listFieldAt(path)?._order;
  if (!order) return [own];
  return [
    own,
    {
      value: `${path}[0]`,
      label: `${path}[0]`,
      description: `The first one — in ${order}`,
      category: path.split('.')[0],
    },
  ];
}

function suggestionForPath(path: string): CatalogSuggestion {
  const description = resolveSchemaPath(path);
  const children = childSegmentsOf(path);
  const marks = [description, children && children.length > 0 ? `${children.length} fields` : undefined];
  return markSuggestion({ value: path, label: path, description, category: path.split('.')[0] }, marks);
}

/**
 * **The path type-down, read as a location where the query names one** (`#1249`, Todd 2026-09-04:
 * *"Can the autocomplete itself be parent/child aware?"*).
 *
 * Measured 2026-09-11, before the change: typing `session.` — the gesture that means *show me what
 * is under session* — returned **52** rows of which 16 were its children and the rest
 * grandchildren, and the list was silently cut at 50. The empty box returned **100** rows on the
 * two surfaces that open it on focus.
 *
 * ## Two behaviours, one question
 *
 * They are **not modes to choose between**: they are *show me what is here* and *find me this
 * wherever it lives*, and which one is being asked is decided by whether the query names a
 * location.
 *
 * | The query | Reads as | Offers |
 * | --- | --- | --- |
 * | empty | the top of the model | the six roots |
 * | names a branch (`session`, `user.`, `session.content`) | that location | its children, one segment deep |
 * | a partial segment under one (`session.con`) | that location, filtered | the siblings matching |
 * | a bare token (`max`, `ua`, `{ip}`) | a search | every path matching, anywhere |
 *
 * **A branch descends when it is selected**, because selecting one puts its own path in the box
 * and that path names a location — so the gesture needs no instruction and no trailing-dot
 * spelling to store by accident. Typing the `.` yourself works for the same reason.
 *
 * ## What it must not break, and does not
 *
 * - **`pathsForMacroQuery`** — a path reachable by typing its macro name (`ua` →
 *   `session.user.userAgent`) is a **bare-token** behaviour and lives untouched in the search leg.
 * - **Aliases descend.** `user.` is canonicalised before the location is read, or the spelling an
 *   operator actually types would stop working one keystroke in (`#1213`).
 * - **A declared exclusion stays out.** `childSegmentsOf` skips `notOffered` exactly as
 *   `getAllFieldPaths` does, so `demandSource.headers` is no more offered here than it was
 *   (`#1217`).
 * - **{@link ALIAS_PATH_SUGGESTIONS} stays in the search leg only.** An alias is an *input
 *   spelling*, not a member of the model (`#1213`), so enumerating `session.content.` must offer
 *   the canonical `genres` and not both spellings of it. Typing `genre` still finds it, which is
 *   the moment it helps.
 *
 * ## The cap is gone rather than disclosed
 *
 * `ranked.slice(0, 50)` cut the empty query to 50 of 152 and any single-letter query to 50 of
 * more, with nothing saying so — a list that looks complete and is not (`#1045`). Segment-scoping
 * removes the reason it existed: no level of the model is anywhere near 50, and the widest search
 * a bare token can produce is the 152 paths themselves, which is a native `<datalist>`'s ordinary
 * business. A cap kept *and* disclosed would need a row that is not a suggestion, in a control
 * where every row is selectable.
 */
export function getSchemaSuggestions(query: string): CatalogSuggestion[] {
  const trimmed = query.trim();
  const normalizedQuery = normalizeSearchToken(trimmed);

  // An alias is an input spelling, resolved before the query is read as a location (`#1213`).
  const canonical = canonicalizeContextPath(trimmed);
  const location = locationFor(canonical);
  if (location) return location;

  const macroPaths = pathsForMacroQuery(normalizedQuery);
  const matches = CATALOG_PATHS.filter((path) => {
    const normalizedPath = normalizeSearchToken(path);
    const normalizedDescription = normalizeSearchToken(resolveSchemaPath(path));
    return (
      normalizedPath.includes(normalizedQuery) ||
      path.toLowerCase().includes(trimmed.toLowerCase()) ||
      normalizedDescription.includes(normalizedQuery)
    );
  }).sort((a, b) => {
    const aStarts = normalizeSearchToken(a).startsWith(normalizedQuery);
    const bStarts = normalizeSearchToken(b).startsWith(normalizedQuery);
    if (aStarts && !bStarts) return -1;
    if (!aStarts && bStarts) return 1;
    return a.localeCompare(b);
  });

  // A path the query names by its macro leads, and is included even when the text
  // search would not have found it at all — that is the whole point.
  const ranked = [...macroPaths, ...matches.filter((path) => !macroPaths.includes(path))];

  const aliasMatches = ALIAS_PATH_SUGGESTIONS.filter(
    (item) =>
      normalizeSearchToken(item.value).includes(normalizedQuery) ||
      normalizeSearchToken(item.description).includes(normalizedQuery),
  );

  const helperMatches = MACRO_HELPER_SUGGESTIONS.filter(
    (item) =>
      normalizeSearchToken(item.value).includes(normalizedQuery) ||
      normalizeSearchToken(item.description).includes(normalizedQuery),
  );

  return [...ranked.flatMap(suggestionsForPath), ...aliasMatches, ...helperMatches];
}

/**
 * **Is this query a location rather than a search?** — asked by {@link getValueSuggestions} so the
 * macro half is offered on a search and withheld on a walk.
 *
 * It delegates to {@link locationFor} rather than restating the test, because *which queries name a
 * location* is one rule and a second implementation of it would drift the moment either changed —
 * a reader narrower than its writer, which is how `#1269` happened.
 */
export function readsAsLocation(query: string): boolean {
  return locationFor(canonicalizeContextPath(query.trim())) !== null;
}

/**
 * **The schema as a tree for the shared location leg** (`path-location.ts`, `#1580`). The walk is one
 * rule for every path type-down; this is what the session schema supplies to it.
 */
const SCHEMA_TREE: PathTree = {
  childrenOf: childSegmentsOf,
  rowsFor: suggestionsForPath,
  openBranch: (base) =>
    isOpenContextRoot(base)
      ? [
          markSuggestion(
            {
              value: `${base}.`,
              label: base,
              description: CONTEXT_ROOTS.find((r) => r.name === base)?.description ?? '',
              category: base,
            },
            [OPEN_ROOT_HAS_NO_LIST],
          ),
        ]
      : null,
};

function locationFor(canonical: string): CatalogSuggestion[] | null {
  return locationIn(SCHEMA_TREE, canonical);
}

/**
 * Split a field name into lowercase tokens, breaking on separators and camelCase
 * so `pub_genre` → [pub, genre] and `maxDuration` → [max, duration].
 */
function tokenize(name: string): string[] {
  return name
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean)
    .map((s) => s.toLowerCase());
}

/**
 * Match a parameter/leaf name against the shared macro vocabulary — **the** matcher.
 *
 * Exact match wins; otherwise a macro matches only when it equals a **whole token**
 * of the key, never an arbitrary substring. That rule was already written once, in
 * `mapping-suggest`'s private `matchMacro`, while this module kept a raw
 * `String.includes` fallback — and the console therefore had two matchers that
 * disagreed about the same key, with the wrong one wired to the row builder.
 *
 * What the substring version did to a real Publica tag (#1003), first match winning
 * by object key order:
 *
 * | Parameter | Matched | Because |
 * |---|---|---|
 * | `position` | `os` → `session.user.device.os` | p-**os**-ition |
 * | `content_language` | `ua` → `session.user.userAgent` | lang-**ua**-ge |
 * | `content_length` | `h` → `session.context.playerHeight` | content_lengt-**h** |
 *
 * Every one produced a plausible value on the wire — the OS string in `position`,
 * a user agent in `content_language` — which is why nothing caught it. With
 * one-and-two-character macros in the vocabulary (`ip`, `ua`, `os`, `w`, `h`), a
 * substring rule is not a loose match; it is a random one.
 */
/**
 * The one macro name the editor **suggests** for each concept (#922-class, ratified by
 * Todd 2026-08-07: *"I wanted only the 'correct'/best macro offered"*).
 *
 * ## Named for its role, because the vocabulary has three tiers and this set spans two
 *
 * Todd, 2026-08-07: *"Canonical [is] the industry standard and accepted — whereas Default
 * is the terms we've decided within the system, or the user sets as their vocabulary."*
 * Both words are load-bearing and they are **not** synonyms:
 *
 * | Tier | Whose word it is | Examples |
 * |---|---|---|
 * | **canonical** | the industry — IAB / OpenRTB / VAST | `ip`, `ua`, `ifa`, `w`, `h`, `osv`, `gdpr_consent`, `us_privacy`, `dnt`, `lmt`, `coppa`, `app_bundle` |
 * | **default** | ours, where no standard exists | `pod_max_ads`, `pod_max_duration`, `device_name`, `content_title` |
 * | **the deployment's** | the operator's own, incl. `session.custom.*` | whatever their clients already say (#751) |
 *
 * This set is the first two tiers, and the selection rule is **canonical where a standard
 * exists, our default where none does** — which is why `ifa` beat `idfa`/`did` (OpenRTB
 * names the field `device.ifa`) but `pod_max_ads` is simply our choice, there being no
 * industry name for it.
 *
 * Calling the whole thing `CANONICAL_` would claim industry backing for names we invented;
 * calling it `DEFAULT_` would demote the ones the industry actually settled. So it is named
 * for what it *does* — these are the names the editor suggests.
 *
 * ## Why the tiering matters beyond naming
 *
 * Preferring the industry word is not neutrality, it is the point: it is *"understandable
 * to the widest audience, and users already know that mental map"*. And the third tier is
 * why this must never harden into "the one true vocabulary" — an operator learns our names
 * during the mapping exercises and **has to believe their own values live alongside ours
 * rather than in a second-class annexe**. See `INBOUND_VOCABULARY_DESIGN.md`.
 *
 * ## What this narrows, and what it does not
 *
 * `MACRO_DEFAULTS` stays wide because it is also the *matcher's* vocabulary: a pasted tag
 * names its parameter whatever the exchange calls it, and we want to match `device_id` as
 * readily as `ifa`. Offering all 50 of those spellings for 32 concepts put `{ip}` beside
 * `{ipAddress}` and four names for one device id — a decision the operator has no
 * information to make, and one where two people pick differently and their sources stop
 * looking alike.
 *
 * **Every alias still resolves, still matches, and still renders as a known macro.** It
 * only stops being suggested.
 *
 * The picks, where more than one name existed:
 *
 * | Concept | Chosen | Over | Why |
 * |---|---|---|---|
 * | IP / UA | `ip`, `ua` | `ipAddress`, `userAgent` | what real VAST tags carry |
 * | device id | `ifa` | `idfa`, `did`, `device_id` | OpenRTB's own field name; `idfa` is Apple-specific and misleading on a Roku. `did` is historical (Todd) |
 * | OS version | `osv` | `os_version` | OpenRTB `device.osv` |
 * | player size | `w`, `h` | `width`/`player_width`, `height`/`player_height` | ORTB `video.w`/`video.h`, and the short form is what tags use |
 * | content length | `content_length` | `dur` | `dur` reads as *which* duration — the ambiguity behind #1014 |
 * | genres | `genres` | `genre` | it is an array; the singular misleads |
 * | content rating | `content_rating` | `rating`, `contentRating` | `rating` is ambiguous (content rating vs. a score) |
 * | the rest | snake_case | camelCase | the vocabulary is predominantly snake; one style |
 *
 * `resolvable-macros.test.ts` asserts **exactly one suggested name per path**, so adding an
 * alias without deciding which is best fails the build, and so does adding a concept and
 * forgetting to mark one.
 */
export const SUGGESTED_MACRO_NAMES: ReadonlySet<string> = new Set(suggestedNames());

/**
 * The macro the editor suggests by default for whatever concept `name` names.
 *
 * Used when *seeding* a row from a parameter/header name (`rowForParamName`,
 * `headerRowForKey`): matching is deliberately wide, so a param called `device_id`
 * matches — but what gets written into the row should be the name we would have
 * suggested, `{ifa}`, not the alias that happened to match. Seeding is an offer, and
 * the same rule applies to it as to the picker.
 */
export function suggestedMacroFor(name: string): { macro: string; path?: string } | null {
  const match = matchMacroName(name);
  if (!match) return null;
  if (SUGGESTED_MACRO_NAMES.has(match.macro)) return { macro: match.macro, path: match.path };
  // **The preferred sibling is found by concept, not by path** (#1190). Looking it up by
  // path could never reach a produced or chained concept — `cb` and `schain` have no path to
  // compare — so the one case that most needs a canonical spelling was the one it skipped.
  const preferred = [...MACRO_VOCABULARY].find(
    ([macro, entry]) => entry.name === match.concept.name && SUGGESTED_MACRO_NAMES.has(macro),
  );
  return preferred ? { macro: preferred[0], path: preferred[1].path } : { macro: match.macro, path: match.path };
}

/**
 * **A match returns the concept, not the spelling that found it** (#1186).
 *
 * The vocabulary lists every spelling a pasted tag might carry — `ad_unit`, `adunit` and
 * `tagid` all name `session.adUnit` — so answering with the matched *key* answered *which
 * spelling was in the tag*, and the row was written with that one. Two tags carrying the same
 * concept produced `{tagid}` and `{ad_unit}`, both resolving, and an operator comparing two
 * sources saw two things. Todd's ruling: *"for vast let's make the macro `{ad_unit}`."*
 *
 * **It used to need a lookup and now it does not** (#1190). `canonicalMacro` resolved the
 * matched spelling back through `catalogEntryFor`, and carried a caveat — *a name the catalog
 * does not know keeps its own spelling, because `MACRO_DEFAULTS` carries a few the catalog
 * does not*. That caveat described the drift, not a rule: now the vocabulary **is** the
 * catalog, every match already holds its concept, and the case the caveat covered cannot
 * arise. The `concept` is returned too, so a caller needing more than a name and a path — the
 * `chain` flag, `perRequest`, `consent` — asks the entry rather than a fourth lookup.
 */
export function matchMacroName(
  name: string,
): { macro: string; path?: string; exact: boolean; concept: CatalogConcept } | null {
  const lower = name.trim().toLowerCase();
  if (!lower) return null;

  for (const [macro, entry] of MACRO_VOCABULARY) {
    if (macro.toLowerCase() === lower) return { macro: entry.name, path: entry.path, exact: true, concept: entry };
  }

  const tokens = tokenize(name);
  for (const run of MACRO_TOKEN_RUNS) {
    if (containsTokenRun(tokens, run.tokens)) {
      return { macro: run.concept.name, path: run.concept.path, exact: false, concept: run.concept };
    }
  }

  return null;
}

/**
 * **A whole token, or a whole *run* of them** (#1189).
 *
 * The rule above was written against a single-token vocabulary — `ip`, `ua`, `os` — and read
 * `tokens.has(macro)`, so a macro spelled with more than one word could only ever match a key
 * that equalled it **exactly**. Two thirds of the vocabulary is spelled that way, and the
 * consequence is invisible from either side: `pod_id` is in the list, `unique_pod_id` tokenizes
 * to `[unique, pod, id]`, and `has('pod_id')` is false. So the concept was reachable by a key
 * named nothing but `pod_id`, and a real body names it `kvps.unique_pod_id`.
 *
 * Matching a contiguous run keeps the whole property #1003 was about — a macro still never
 * matches part of a word, which is what stopped `position` resolving to `os` — and extends it
 * to the names the vocabulary actually contains.
 *
 * **Longest run wins**, which is the same *most specific first* precedence the exact match
 * already has over a token match. Without it `device_ifa_type` would resolve by whichever of
 * `ifa` and `ifa_type` the object happened to list first — object key order deciding a
 * mapping is exactly the #1003 failure, one layer along.
 */
const MACRO_TOKEN_RUNS: readonly { macro: string; concept: CatalogConcept; tokens: readonly string[] }[] = [
  ...MACRO_VOCABULARY,
]
  .map(([macro, concept]) => ({ macro, concept, tokens: tokenize(macro) }))
  // Stable, so names of equal specificity keep the catalog's own order.
  .sort((a, b) => b.tokens.length - a.tokens.length);

/** Whether `run` appears as a contiguous sequence of `tokens`. */
function containsTokenRun(tokens: readonly string[], run: readonly string[]): boolean {
  if (run.length === 0 || run.length > tokens.length) return false;
  for (let i = 0; i + run.length <= tokens.length; i += 1) {
    let matched = true;
    for (let j = 0; j < run.length; j += 1) {
      if (tokens[i + j] !== run[j]) {
        matched = false;
        break;
      }
    }
    if (matched) return true;
  }
  return false;
}

/** The context path a parameter name maps to, via {@link matchMacroName}. */
export function getMacroDefaultPath(paramName: string): string | null {
  return matchMacroName(paramName)?.path ?? null;
}

// ---------------------------------------------------------------------------
// Static Catalog Builder
// ---------------------------------------------------------------------------

import { catalogEntryFor, inferableSpellings, suggestedNames } from '@field-catalog';
// **Aliased, because this file already imports a different `FieldCatalogEntry`** — the
// console's own picker-facing type from `types/field-catalog`. Two types, one name, one
// module: the catalog's entry is the *concept* a spelling names, so it is imported as one.
import type { FieldCatalogEntry as CatalogConcept } from '@field-catalog';
import type { FieldCatalogEntry, FieldCatalogCategory } from '../../../types/field-catalog';

export type { FieldCatalogEntry };

/**
 * Build a FieldCatalogEntry array from the static schema paths and macros.
 * This is the authoritative local catalog; the backend may augment it with
 * custom/dynamic entries (pixels, extractions, user-defined fields).
 */
export function buildStaticCatalog(): FieldCatalogEntry[] {
  const entries: FieldCatalogEntry[] = [];

  const topLevelCategoryMap: Record<string, FieldCatalogCategory> = {
    session: 'session',
    pod: 'pod',
    demandSource: 'demandSource',
    content: 'content',
    user: 'user',
    context: 'context',
  };

  CATALOG_PATHS.forEach((path) => {
    const description = resolveSchemaPath(path);
    const topLevel = path.split('.')[0];
    const category: FieldCatalogCategory = topLevelCategoryMap[topLevel] ?? 'custom';

    entries.push({
      value: path,
      label: path,
      description,
      category,
      source: 'schema',
    });
  });

  // Add alias suggestions
  ALIAS_PATH_SUGGESTIONS.forEach((alias) => {
    const topLevel = alias.value.split('.')[0];
    entries.push({
      value: alias.value,
      label: alias.value,
      description: alias.description,
      category: (topLevel as FieldCatalogCategory) ?? 'custom',
      source: 'schema',
    });
  });

  // Add macro helpers
  MACRO_HELPER_SUGGESTIONS.forEach((m) => {
    entries.push({
      value: m.value,
      label: m.value,
      description: m.description,
      category: 'macro',
      source: 'macro',
    });
  });

  return entries;
}

/**
 * Merge static catalog entries with dynamic entries supplied at runtime
 * (e.g. from the backend catalog API or pixel-derived fields).
 * Dynamic entries with the same `value` key override static ones.
 */
export function mergeCatalogEntries(
  staticEntries: FieldCatalogEntry[],
  dynamicEntries: FieldCatalogEntry[],
): FieldCatalogEntry[] {
  const seen = new Map<string, FieldCatalogEntry>();
  staticEntries.forEach((e) => seen.set(e.value, e));
  dynamicEntries.forEach((e) => seen.set(e.value, e)); // dynamic wins
  return Array.from(seen.values());
}

/**
 * Convert a FieldCatalogEntry list to CatalogSuggestion list for use with
 * existing autocomplete components.
 */
export function catalogEntriesToSuggestions(entries: FieldCatalogEntry[], query: string): CatalogSuggestion[] {
  const normalizedQuery = normalizeSearchToken(query.trim());

  const filtered = normalizedQuery
    ? entries.filter((e) => {
        return (
          normalizeSearchToken(e.value).includes(normalizedQuery) ||
          e.value.toLowerCase().includes(query.toLowerCase()) ||
          normalizeSearchToken(e.description).includes(normalizedQuery) ||
          (e.tags ?? []).some((t) => normalizeSearchToken(t).includes(normalizedQuery))
        );
      })
    : entries;

  filtered.sort((a, b) => {
    const aStarts = normalizeSearchToken(a.value).startsWith(normalizedQuery);
    const bStarts = normalizeSearchToken(b.value).startsWith(normalizedQuery);
    if (aStarts && !bStarts) return -1;
    if (!aStarts && bStarts) return 1;
    return a.value.localeCompare(b.value);
  });

  return filtered.slice(0, 50).map((e) => ({
    value: e.value,
    label: e.label,
    description: e.description,
    category: e.category,
  }));
}
